import FormFieldComponent from "./FormFieldComponent";
import { IoIosAddCircle } from "react-icons/io";
import React from "react";
import { QuestionAnalysis } from "../models/QuestionAnalysis";
import "./questionanalysisform.css";
import {
  Button,
  Flex,
  Group,
  NumberInput,
  Stack,
  Text,
  Title,
} from "@mantine/core";

interface QuestionAnalysisFormProps {
  questionAnalysis: QuestionAnalysis;
  setQuestionAnalysis: (
    newState: (prev: QuestionAnalysis) => QuestionAnalysis,
  ) => void;
  setEdited: (newValue: boolean) => void;
  currentlyEditing: boolean;
  handleDoneEditing: (updatedAnalysis: QuestionAnalysis) => void;
  handleAddAnalysis: () => void;
  interviewSubmitted: boolean;
}

const QuestionAnalysisForm = ({
  questionAnalysis,
  setEdited,
  setQuestionAnalysis,
  currentlyEditing,
  handleDoneEditing,
  handleAddAnalysis,
  interviewSubmitted,
}: QuestionAnalysisFormProps) => {
  const handleAnalysisInputChange = (field: string, value: string) => {
    setEdited(true);
    if (
      field === "onPointScore" ||
      field === "conciseScore" ||
      field === "deliveryScore"
    ) {
      if (Number(value) >= 0 && Number(value) <= 5) {
        setQuestionAnalysis((prev: QuestionAnalysis) => ({
          ...prev,
          [field]: value,
        }));
      }
    } else {
      setQuestionAnalysis((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  return (
    <Flex h="100%" direction="column" w="40%">
      <Title order={4}>Add an Analysis:</Title>
      <Stack h="100%">
        <FormFieldComponent
          label="Question:"
          placeholder="Tell me about yourself."
          value={questionAnalysis.question}
          onChange={handleAnalysisInputChange}
          field="question"
          type="textarea"
          disabled={interviewSubmitted}
        />
        <FormFieldComponent
          label="User's Answer:"
          placeholder="I have worked at company XYZ for 5 years..."
          value={questionAnalysis.userAnswer}
          onChange={handleAnalysisInputChange}
          field="userAnswer"
          type="textarea"
          disabled={interviewSubmitted}
        />
        <Group>
          <NumberInput
            styles={{ root: { display: "block" } }}
            w={75}
            max={5}
            min={1}
            label="On Point:"
            value={questionAnalysis.onPointScore.toString()}
            onChange={(value) =>
              handleAnalysisInputChange("onPointScore", value as string)
            }
            disabled={interviewSubmitted}
          />
          <Text pt="lg">/5</Text>
          <Flex flex={1}>
            <FormFieldComponent
              placeholder="Feedback for staying on point..."
              value={questionAnalysis.onPointFeedback}
              onChange={handleAnalysisInputChange}
              field="onPointFeedback"
              type="textarea"
              disabled={interviewSubmitted}
            />
          </Flex>
        </Group>
        <Group>
          <NumberInput
            styles={{ root: { display: "block" } }}
            w={75}
            max={5}
            min={1}
            label="Concise:"
            value={questionAnalysis.conciseScore.toString()}
            onChange={(value) =>
              handleAnalysisInputChange("conciseScore", value as string)
            }
            disabled={interviewSubmitted}
          />
          <Text pt="lg">/5</Text>
          <Flex flex={1}>
            <FormFieldComponent
              placeholder="Feedback for being concise..."
              value={questionAnalysis.conciseFeedback}
              onChange={handleAnalysisInputChange}
              field="conciseFeedback"
              type="textarea"
              disabled={interviewSubmitted}
            />
          </Flex>
        </Group>
        <Group>
          <NumberInput
            styles={{ root: { display: "block" } }}
            w={75}
            max={5}
            min={1}
            label="Delivery:"
            value={questionAnalysis.deliveryScore.toString()}
            onChange={(value) =>
              handleAnalysisInputChange("deliveryScore", value as string)
            }
            disabled={interviewSubmitted}
          />
          <Text pt="lg">/5</Text>
          <Flex flex={1}>
            <FormFieldComponent
              placeholder="Feedback for user delivery..."
              value={questionAnalysis.deliveryFeedback}
              onChange={handleAnalysisInputChange}
              field="deliveryFeedback"
              type="textarea"
              disabled={interviewSubmitted}
            />
          </Flex>
        </Group>
        <FormFieldComponent
          label="Alternative Answer:"
          placeholder="A better answer to the question is..."
          value={questionAnalysis.alternativeAnswer}
          onChange={handleAnalysisInputChange}
          field="alternativeAnswer"
          type="textarea"
          disabled={interviewSubmitted}
        />
        {!interviewSubmitted && (
          <Button
            mt="auto"
            rightSection={<IoIosAddCircle />}
            onClick={
              currentlyEditing
                ? () => handleDoneEditing(questionAnalysis)
                : handleAddAnalysis
            }
          >
            {currentlyEditing ? "Done editing" : "Add question"}
          </Button>
        )}
      </Stack>
    </Flex>
  );
};

export default QuestionAnalysisForm;
